import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllMSPs, getMSPDetails, createTenantForMSP, assignTenantToMSP, getMSPTenants, getAllTenants } from '../utils/adminApi';
import toastr from 'toastr';

const API_BASE = process.env.REACT_APP_TENANT_API || 'https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod';

const MSPList = () => {
  const [msps, setMsps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMSPs();
  }, []);

  const loadMSPs = async () => {
    try {
      const mspsData = await getAllMSPs();
      setMsps(mspsData);
    } catch (error) {
      console.error('Error loading MSPs:', error);
      toastr.error('Failed to load MSPs');
      setMsps([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Managed Service Providers</h2>
        <Link to="/dashboard/create-msp" className="btn btn-primary">
          <i className="fas fa-plus me-2"></i>
          Create MSP
        </Link>
      </div>

      {msps.length === 0 ? (
        <div className="alert alert-info">
          <strong>No MSPs found.</strong> Create your first MSP to get started.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Subdomain</th>
                <th>Admin Email</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {msps.map(msp => (
                <MSPRow key={msp.mspID} msp={msp} onRefresh={loadMSPs} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const MSPRow = ({ msp, onRefresh }) => {
  const [showTenants, setShowTenants] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [allTenants, setAllTenants] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const data = await getMSPTenants(msp.mspID);
      setTenants(data.tenants || []);
    } catch (error) {
      toastr.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const loadAllTenants = async () => {
    try {
      const tenants = await getAllTenants();
      setAllTenants(tenants);
    } catch (error) {
      toastr.error('Failed to load available tenants');
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await createTenantForMSP(
        msp.mspID,
        formData.get('email'),
        formData.get('name')
      );
      toastr.success('Tenant created successfully');
      setShowCreateForm(false);
      loadTenants();
      e.target.reset();
    } catch (error) {
      toastr.error(error.message);
    }
  };

  const handleAssignTenant = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await assignTenantToMSP(msp.mspID, formData.get('tenantID'));
      toastr.success('Tenant assigned successfully');
      setShowAssignForm(false);
      loadTenants();
    } catch (error) {
      toastr.error(error.message);
    }
  };

  const handleRemoveTenant = async (tenantID) => {
    if (!window.confirm('Are you sure you want to remove this tenant from the MSP?')) {
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/v2/msp/tenants/unassign?mspID=${msp.mspID}&tenantID=${tenantID}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to remove tenant');
      toastr.success('Tenant removed successfully');
      loadTenants();
    } catch (error) {
      toastr.error(error.message);
    }
  };

  const toggleTenants = () => {
    if (!showTenants) {
      loadTenants();
    }
    setShowTenants(!showTenants);
  };

  return (
    <>
      <tr>
        <td>{msp.name}</td>
        <td><code>{msp.slug}</code></td>
        <td>{msp.subdomain}</td>
        <td>{msp.email}</td>
        <td>
          <span className={`badge ${msp.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
            {msp.status}
          </span>
        </td>
        <td>{new Date(msp.createdAt).toLocaleDateString()}</td>
                  <td>
                    <button className="btn btn-sm btn-outline-primary me-1" onClick={toggleTenants}>
                      {showTenants ? 'Hide' : 'View'} Tenants
                    </button>
                    <button className="btn btn-sm btn-success me-1" onClick={() => setShowCreateForm(true)}>
                      Create Tenant
                    </button>
                    <button className="btn btn-sm btn-warning" onClick={() => { setShowAssignForm(true); loadAllTenants(); }}>
                      Assign Tenant
                    </button>
                  </td>
      </tr>
      {showTenants && (
        <tr>
          <td colSpan="7">
            <div className="p-3 bg-light">
              <h6>MSP Tenants {loading && <span className="spinner-border spinner-border-sm ms-2"></span>}</h6>
              {tenants.length === 0 ? (
                <p className="text-muted">No tenants assigned to this MSP</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Tenant ID</th>
                        <th>Created</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenants.map(tenant => (
                        <tr key={tenant.tenantID}>
                          <td>{tenant.email}</td>
                          <td><code>{tenant.tenantID}</code></td>
                          <td>{new Date(tenant.createdAt).toLocaleDateString()}</td>
                          <td>
                            <span className="badge bg-success">
                              {tenant.subscriptionPlan?.status || 'active'}
                            </span>
                          </td>
                          <td>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleRemoveTenant(tenant.tenantID)}>
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
      {showCreateForm && (
        <tr>
          <td colSpan="7">
            <div className="p-3 bg-info bg-opacity-10">
              <h6>Create New Tenant for {msp.name}</h6>
              <form onSubmit={handleCreateTenant} className="row g-3">
                <div className="col-md-4">
                  <input type="email" name="email" className="form-control" placeholder="Customer Email" required />
                </div>
                <div className="col-md-4">
                  <input type="text" name="name" className="form-control" placeholder="Customer Name" />
                </div>
                <div className="col-md-4">
                  <button type="submit" className="btn btn-success me-2">Create</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowCreateForm(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </td>
        </tr>
      )}
      {showAssignForm && (
        <tr>
          <td colSpan="7">
            <div className="p-3 bg-warning bg-opacity-10">
              <h6>Assign Existing Tenant to {msp.name}</h6>
              <form onSubmit={handleAssignTenant} className="row g-3">
                <div className="col-md-6">
                  <select name="tenantID" className="form-select" required>
                    <option value="">Select Tenant...</option>
                    {allTenants.filter(t => t.managedBy !== 'msp').map(tenant => (
                      <option key={tenant.tenantID} value={tenant.tenantID}>
                        {tenant.email} | {tenant.subscriptionPlan?.plan || 'Basic'} | Created: {new Date(tenant.createdAt).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <button type="submit" className="btn btn-warning me-2">Assign</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAssignForm(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default MSPList;